// One requirement: status, the "Learn more" guide, and the actions a business
// can take. None of these actions can verify anything: verification is done
// by a ConfirmPH reviewer, and the panel says so where it matters.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  LEVEL_LABELS, errorMessage, fileToBase64, lrGet, lrPatch, lrPost, statusLabel, statusTone,
} from "../../lib/clientLegal";
import { Button, Empty, Field, Loading, Modal, Note, TonePill, inputClass } from "../platform-legal/ui";

export function StatusPill({ status, testId }) {
  return (
    <TonePill tone={statusTone(status)} testId={testId}>{statusLabel(status)}</TonePill>
  );
}

export default function RequirementDetail({ requirementKey, scopeId, onClose, onChanged, options }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);

  const params = scopeId ? { params: { scope_id: scopeId } } : undefined;
  const load = useCallback(() => lrGet(`/requirements/${requirementKey}`, params)
    .then(setData).catch((err) => toast.error(errorMessage(err))),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [requirementKey, scopeId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, message) => {
    setBusy(true);
    try {
      await fn();
      toast.success(message);
      setMode(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <Modal title="Loading requirement" onClose={onClose}><Loading /></Modal>;
  }
  const { requirement: r, guide, evidence, history } = data;
  const q = scopeId ? `?scope_id=${encodeURIComponent(scopeId)}` : "";

  return (
    <Modal title={r.title} description={r.authority} onClose={onClose} testId="cl-requirement-modal">
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill status={r.status} testId="cl-req-status" />
        <TonePill tone="border-slate-200 bg-white text-slate-700" testId="cl-req-level">
          {LEVEL_LABELS[r.verification_level] || r.verification_level}
        </TonePill>
        {r.criticality === "CRITICAL_BLOCKER" && (
          <TonePill tone="border-slate-300 bg-slate-100 text-slate-800">Critical requirement</TonePill>
        )}
      </div>
      {r.reason && (
        <Note tone={r.blocking ? "rose" : "amber"} testId="cl-req-reason">
          <span className="font-semibold">{r.reason.title}.</span> {r.reason.explanation}
          {r.reason.next_action ? ` Next: ${r.reason.next_action}.` : ""}
        </Note>
      )}
      {r.applicability_reason && <p className="text-xs text-slate-600">{r.applicability_reason}</p>}
      {r.review_message && (
        <Note tone="sky" testId="cl-review-message">Message from ConfirmPH review: {r.review_message}</Note>
      )}
      {r.verified_at && (
        <p className="text-xs text-slate-600" data-testid="cl-req-verified">
          ConfirmPH reviewed the evidence on {r.verified_at.slice(0, 10)}
          {r.verified_through ? `; valid through ${r.verified_through}` : ""}. This is not an agency
          confirmation unless it says so.
        </p>
      )}

      {data.can_write && !mode && (
        <div className="flex flex-wrap gap-2" data-testid="cl-req-actions">
          <Button variant="secondary" onClick={() => setMode("details")} data-testid="cl-act-details">
            Record what you have
          </Button>
          {data.can_upload && (
            <Button variant="secondary" onClick={() => setMode("evidence")} data-testid="cl-act-upload">
              Upload evidence
            </Button>
          )}
          {r.evidence_count > 0 && r.status !== "UNDER_REVIEW" && (
            <Button busy={busy} data-testid="cl-act-request-review"
                    onClick={() => act(() => lrPost(`/requirements/${r.key}/request-review${q}`, {}),
                                       "Sent for ConfirmPH review.")}>
              Request ConfirmPH review
            </Button>
          )}
          <Button variant="ghost" onClick={() => setMode("not_applicable")} data-testid="cl-act-na">
            Doesn't apply to us
          </Button>
          <Button variant="ghost" onClick={() => setMode("regularization")} data-testid="cl-act-regularize">
            Already operating without this?
          </Button>
        </div>
      )}
      {mode === "details" && (
        <DetailsForm r={r} busy={busy} options={options} onCancel={() => setMode(null)}
                     onSave={(payload) => act(() => lrPatch(`/requirements/${r.key}${q}`, payload),
                                              "Recorded. This is self-reported until evidence is reviewed.")} />
      )}
      {mode === "evidence" && (
        <EvidenceForm busy={busy} options={options} onCancel={() => setMode(null)}
                      onSave={(payload) => act(() => lrPost(`/requirements/${r.key}/evidence${q}`, payload),
                                               "Evidence uploaded. It is not verified until ConfirmPH reviews it.")} />
      )}
      {mode === "not_applicable" && (
        <NotApplicableForm busy={busy} critical={r.criticality === "CRITICAL_BLOCKER"} onCancel={() => setMode(null)}
                           onSave={(payload) => act(() => lrPost(`/requirements/${r.key}/not-applicable${q}`, payload),
                                                    "Recorded with your reason.")} />
      )}
      {mode === "regularization" && (
        <RegularizationForm busy={busy} onCancel={() => setMode(null)}
                            onSave={(payload) => act(() => lrPost(`/requirements/${r.key}/regularization${q}`, payload),
                                                     "Regularization facts recorded.")} />
      )}

      <Guide guide={guide} />

      <section>
        <h4 className="text-sm font-semibold text-slate-900">Evidence</h4>
        {evidence.length === 0 ? <Empty>No evidence uploaded yet.</Empty> : (
          <ul className="mt-2 space-y-1" data-testid="cl-evidence-list">
            {evidence.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs">
                <span>Version {e.version} · {e.document_type.replace(/_/g, " ")} · {e.uploaded_at?.slice(0, 10)}</span>
                <span className="flex items-center gap-2">
                  <TonePill tone="border-slate-200 bg-slate-50 text-slate-700">{e.status}</TonePill>
                  {data.can_open_documents && (
                    <button type="button" className="underline" onClick={() => downloadEvidence(e)}>Open</button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h4 className="text-sm font-semibold text-slate-900">History</h4>
        {history.length === 0 ? <Empty>No history yet.</Empty> : (
          <ol className="mt-2 space-y-1 text-xs text-slate-700" data-testid="cl-history">
            {history.map((h) => (
              <li key={h.id}><span className="text-slate-500">{h.at?.slice(0, 16).replace("T", " ")}</span> · {h.summary}
                {h.by_name ? ` · ${h.by_name}` : ""}</li>
            ))}
          </ol>
        )}
      </section>
      <Note tone="slate">{data.disclaimer}</Note>
    </Modal>
  );
}

async function downloadEvidence(e) {
  try {
    const { api } = await import("../../lib/api");
    const res = await api.get(`/compliance/legal-readiness/evidence/${e.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

export function Guide({ guide }) {
  if (!guide) return null;
  return (
    <details className="rounded-xl border border-slate-200 p-3" data-testid="cl-guide">
      <summary className="cursor-pointer text-sm font-semibold text-slate-900">Learn more: how to complete this</summary>
      <dl className="mt-2 grid gap-2 text-sm text-slate-700">
        <GuideRow label="What is this?">{guide.what_it_is}</GuideRow>
        <GuideRow label="Who handles it?">{guide.authority}</GuideRow>
        <GuideRow label="Where do I apply?">{guide.where_to_apply} {guide.online === "yes" ? "(online)" :
          guide.online === "partly" ? "(partly online)" : ""}</GuideRow>
        {guide.documents_needed.length > 0 && (
          <GuideRow label="Documents usually needed">
            <ul className="list-disc pl-5">{guide.documents_needed.map((d) => <li key={d}>{d}</li>)}</ul>
          </GuideRow>
        )}
        {guide.steps.length > 0 && (
          <GuideRow label="Steps">
            <ol className="list-decimal pl-5">{guide.steps.map((d) => <li key={d}>{d}</li>)}</ol>
          </GuideRow>
        )}
        <GuideRow label="Government fee">{guide.fee.note}{guide.fee.source ? ` Source: ${guide.fee.source}.` : ""}</GuideRow>
        <GuideRow label="Expected time">{guide.processing_time.note}</GuideRow>
        <GuideRow label="What you receive">{guide.what_you_receive}</GuideRow>
        <GuideRow label="Expiry and renewal">{guide.renewal_rule}</GuideRow>
        <GuideRow label="Evidence to upload afterwards">{guide.evidence_to_upload.join("; ")}</GuideRow>
        <GuideRow label="If it is missing">{guide.missing_consequence}</GuideRow>
        <GuideRow label="Already operating?">{guide.regularization}</GuideRow>
        <GuideRow label="When to ask a professional">{guide.ask_professional_when}</GuideRow>
        {guide.official_links.length > 0 && (
          <GuideRow label="Official sources">
            <ul className="space-y-0.5">
              {guide.official_links.map((l) => (
                <li key={l.url}><a href={l.url} target="_blank" rel="noopener noreferrer" className="underline">
                  {l.authority}: {l.title}</a></li>
              ))}
            </ul>
          </GuideRow>
        )}
        <GuideRow label="Guidance status">
          {guide.certainty.replace(/_/g, " ").toLowerCase()} · version {guide.version} · {guide.freshness.replace(/_/g, " ").toLowerCase()}
        </GuideRow>
      </dl>
    </details>
  );
}

function GuideRow({ label, children }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

function DetailsForm({ r, busy, options, onCancel, onSave }) {
  const record = r.record || {};
  const [form, setForm] = useState({
    reported_status: record.reported_status || "have_it", reference_number: "",
    issue_date: record.issue_date || "", expiry_date: record.expiry_date || "",
    submitted_date: record.submitted_date || "", owner_notes: record.owner_notes || "", reason: "",
    fields: { ...(record.fields || {}) },
  });
  const payload = () => {
    const out = { ...form };
    ["reference_number", "issue_date", "expiry_date", "submitted_date", "reason"].forEach((k) => {
      if (!out[k]) delete out[k];
    });
    if (!Object.keys(out.fields).length) delete out.fields;
    return out;
  };
  return (
    <div className="space-y-2 rounded-xl border border-slate-200 p-3" data-testid="cl-details-form">
      <Note tone="amber">What you record here is self-reported. It never counts as verified.</Note>
      <Field label="Status">
        <select className={inputClass} value={form.reported_status}
                onChange={(e) => setForm({ ...form, reported_status: e.target.value })}>
          {(options?.reported_statuses || ["have_it", "applied", "not_yet", "renewing"]).map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Reference number" hint={record.reference_masked ? `Stored: ${record.reference_number}` : undefined}>
          <input className={inputClass} value={form.reference_number}
                 onChange={(e) => setForm({ ...form, reference_number: e.target.value })} />
        </Field>
        <Field label="Issue date"><input type="date" className={inputClass} value={form.issue_date}
               onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></Field>
        <Field label="Expiry date"><input type="date" className={inputClass} value={form.expiry_date}
               onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></Field>
        <Field label="Submitted to agency"><input type="date" className={inputClass} value={form.submitted_date}
               onChange={(e) => setForm({ ...form, submitted_date: e.target.value })} /></Field>
      </div>
      {r.fields.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {r.fields.filter((f) => !f.endsWith("_masked")).map((f) => (
            <Field key={f} label={f.replace(/_/g, " ")}>
              {f === "coverage_status" || f === "books_type" ? (
                <select className={inputClass} value={form.fields[f] || ""}
                        onChange={(e) => setForm({ ...form, fields: { ...form.fields, [f]: e.target.value } })}>
                  <option value="">Not recorded</option>
                  {(f === "coverage_status" ? options?.coverage_statuses : options?.books_types || []).map((o) => (
                    <option key={o} value={o}>{o.replace(/_/g, " ").toLowerCase()}</option>
                  ))}
                </select>
              ) : (
                <input className={inputClass} value={form.fields[f] ?? ""}
                       onChange={(e) => setForm({ ...form, fields: { ...form.fields, [f]: e.target.value } })} />
              )}
            </Field>
          ))}
        </div>
      )}
      <Field label="Notes"><textarea className={inputClass} rows={2} value={form.owner_notes}
             onChange={(e) => setForm({ ...form, owner_notes: e.target.value })} /></Field>
      {r.verified_at && (
        <Field label="Reason for changing verified details">
          <input className={inputClass} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </Field>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button busy={busy} onClick={() => onSave(payload())} data-testid="cl-details-save">Save</Button>
      </div>
    </div>
  );
}

function EvidenceForm({ busy, options, onCancel, onSave }) {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ document_type: "permit", reference_number: "", issue_date: "", expiry_date: "" });
  const submit = async () => {
    if (!file) { toast.error("Choose a PDF or image first."); return; }
    const data_base64 = await fileToBase64(file);
    const payload = { data_base64, content_type: file.type || "application/pdf", filename: file.name, ...form };
    Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
    onSave(payload);
  };
  return (
    <div className="space-y-2 rounded-xl border border-slate-200 p-3" data-testid="cl-evidence-form">
      <Note tone="slate">Files are stored privately. Renters never see them; only a summary of what ConfirmPH verified.</Note>
      <Field label="Document">
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" className="text-sm"
               onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cl-evidence-file" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Document type">
          <select className={inputClass} value={form.document_type}
                  onChange={(e) => setForm({ ...form, document_type: e.target.value })}>
            {(options?.document_types || ["permit"]).map((d) => <option key={d} value={d}>{d.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
        <Field label="Reference number"><input className={inputClass} value={form.reference_number}
               onChange={(e) => setForm({ ...form, reference_number: e.target.value })} /></Field>
        <Field label="Issue date"><input type="date" className={inputClass} value={form.issue_date}
               onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></Field>
        <Field label="Expiry date"><input type="date" className={inputClass} value={form.expiry_date}
               onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></Field>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button busy={busy} onClick={submit} data-testid="cl-evidence-save">Upload</Button>
      </div>
    </div>
  );
}

function NotApplicableForm({ busy, critical, onCancel, onSave }) {
  const [form, setForm] = useState({ reason: "", basis: "" });
  return (
    <div className="space-y-2 rounded-xl border border-slate-200 p-3" data-testid="cl-na-form">
      {critical && <Note tone="amber">This is a critical requirement. ConfirmPH reviews the reason before it is treated as not applicable.</Note>}
      <Field label="Why doesn't it apply?"><textarea className={inputClass} rows={2} value={form.reason}
             onChange={(e) => setForm({ ...form, reason: e.target.value })} /></Field>
      <Field label="What fact makes it not apply?"><textarea className={inputClass} rows={2} value={form.basis}
             onChange={(e) => setForm({ ...form, basis: e.target.value })} /></Field>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button busy={busy} onClick={() => onSave(form)} data-testid="cl-na-save">Save</Button>
      </div>
    </div>
  );
}

function RegularizationForm({ busy, onCancel, onSave }) {
  const [form, setForm] = useState({ operating_before_requirement: true, applicable_since: "",
    application_date: "", expected_approval_date: "", note: "" });
  const submit = () => {
    const payload = { ...form };
    Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
    onSave(payload);
  };
  return (
    <div className="space-y-2 rounded-xl border border-slate-200 p-3" data-testid="cl-regularization-form">
      <Note tone="sky">Record the real dates. Backdating a permit, registration, invoice or hiring date is never
        an option here, and dates that have not happened yet are refused.</Note>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Operating since (when this began to apply)"><input type="date" className={inputClass}
               value={form.applicable_since} onChange={(e) => setForm({ ...form, applicable_since: e.target.value })} /></Field>
        <Field label="Application filed on"><input type="date" className={inputClass} value={form.application_date}
               onChange={(e) => setForm({ ...form, application_date: e.target.value })} /></Field>
        <Field label="Expected approval"><input type="date" className={inputClass} value={form.expected_approval_date}
               onChange={(e) => setForm({ ...form, expected_approval_date: e.target.value })} /></Field>
      </div>
      <Field label="Notes"><textarea className={inputClass} rows={2} value={form.note}
             onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button busy={busy} onClick={submit} data-testid="cl-regularization-save">Save</Button>
      </div>
    </div>
  );
}

